import { PaginatedResponse } from "interfaces/paginatedResponse";
import api from "./api";
import type {
  ICupomCategoriaPivot,
  ICupomLoja,
  ICupomLojaCreate,
  ICupomProdutoPivot,
  ICupomUpdate,
} from "interfaces/loja";

// API: Listar os cupons da loja
export async function getAllCuponsLoja(): Promise<
  PaginatedResponse<ICupomLoja>
> {
  try {
    const { data } = await api.get("/loja/cupons");
    return data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Cadastrar um cupom na loja - Retorna o cupom criado (com id) para uso em vínculos.
export async function createCupomLoja(
  data: ICupomLojaCreate
): Promise<ICupomLoja> {
  try {
    const result = await api.post<{ data: ICupomLoja }>("/loja/cupons", data);

    const status = (result as any)?.status;
    if (status != null && status >= 400) {
      const msg = (result as any)?.data?.message ?? "Falha ao criar cupom.";
      throw new Error(msg);
    }
    const body = result.data as { data?: ICupomLoja } | ICupomLoja;
    const cupom = (body as { data?: ICupomLoja }).data ?? (body as ICupomLoja);
    return cupom;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Editar um cupom na loja
export async function updateCupomLoja(cupomId: string, data: ICupomUpdate) {
  try {
    const { data: responseData, status } = await api.put(
      `/loja/cupons/${cupomId}`,
      data
    );
    return { data: responseData, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Deletar um cupom na loja
export async function deleteCupomLoja(cupomId: string) {
  try {
    const { data, status } = await api.delete(`/loja/cupons/${cupomId}`);
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Listar produtos vinculados a um cupom
export async function getCupomProdutosLoja(cupomId: string) {
  try {
    const { data, status } = await api.get<{ data: ICupomProdutoPivot[] }>(
      `/loja/cupons/${cupomId}/produtos`
    );
    return { data, status } as {
      data: { data: ICupomProdutoPivot[] };
      status: number;
    };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Vincular produto pai (todas as SKUs) e/ou SKUs avulsas ao cupom
export async function vincularCupomProdutoLoja(
  cupomId: string,
  body: { produtoIds?: string[]; variacaoIds?: string[] }
) {
  try {
    const url = `/loja/vincular-cupon/${cupomId}/produtos`;
    const { data, status } = await api.post(url, {
      produtoIds: body.produtoIds ?? [],
      variacaoIds: body.variacaoIds ?? [],
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Desvincular produto do cupom (remove pai + todas as SKUs daquele produto)
export async function desvincularCupomProdutoLoja(
  cupomId: string,
  produtoId: string
) {
  try {
    const { data, status } = await api.delete(
      `/loja/desvincular-cupons/cupom/${cupomId}/produto/${encodeURIComponent(produtoId)}`
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Desvincular apenas uma SKU do cupom
export async function desvincularCupomVariacaoLoja(
  cupomId: string,
  variacaoId: string
) {
  try {
    const { data, status } = await api.delete(
      `/loja/desvincular-cupons/cupom/${cupomId}/variacao/${encodeURIComponent(variacaoId)}`
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Listar categorias vinculadas a um cupom
export async function getCupomCategoriasLoja(cupomId: string) {
  try {
    const { data, status } = await api.get<{ data: ICupomCategoriaPivot[] }>(
      `/loja/cupons/${cupomId}/categorias`
    );
    return { data, status } as {
      data: { data: ICupomCategoriaPivot[] };
      status: number;
    };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Vincular categoria ao cupom
export async function vincularCupomCategoriaLoja(
  cupomId: string,
  categoriaId: number
) {
  try {
    const { data, status } = await api.post(
      `/loja/vincular-cupon/${cupomId}/categorias`,
      { categoriaIds: [categoriaId] }
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}

// API: Desvincular categoria do cupom
export async function desvincularCupomCategoriaLoja(
  cupomId: string,
  categoriaId: number
) {
  try {
    const { data, status } = await api.delete(
      `/loja/desvincular-cupons/cupom/${cupomId}/categoria/${categoriaId}`
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação");
  }
}
